/**
 * Persistent thin banner shown above the main content whenever the device
 * is offline OR there are queued mutations waiting to sync. Click "Sync
 * now" to drain immediately. Stays hidden when online and queue empty.
 */
import { useEffect, useState } from "react";
import { WifiOff, RefreshCw, Cloud } from "lucide-react";
import { queueSize } from "@/lib/offlineDb";
import { syncQueue } from "@/lib/syncQueue";

export default function SyncStatusBar() {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [busy, setBusy] = useState(false);

  const refresh = async () => setPending(await queueSize());

  useEffect(() => {
    refresh();
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    const onQueueChanged = () => refresh();
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    window.addEventListener("f2:queue-changed", onQueueChanged);
    const t = setInterval(refresh, 5000);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("f2:queue-changed", onQueueChanged);
      clearInterval(t);
    };
  }, []);

  if (online && pending === 0) return null;

  const doSync = async () => {
    setBusy(true);
    try {
      await syncQueue();
    } finally {
      setBusy(false);
      refresh();
    }
  };

  return (
    <div
      data-testid="sync-status-bar"
      className={`px-4 py-2 text-xs flex items-center justify-between gap-3 border-b ${
        !online
          ? "bg-amber-950/60 border-amber-900/60 text-amber-200"
          : "bg-blue-950/60 border-blue-900/60 text-blue-200"
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {!online ? <WifiOff className="w-3.5 h-3.5 shrink-0" /> : <Cloud className="w-3.5 h-3.5 shrink-0" />}
        <span className="font-display tracking-wider uppercase text-[10px]">
          {!online ? "Offline" : "Online"}
        </span>
        <span className="truncate">
          {pending > 0
            ? `${pending} change${pending === 1 ? "" : "s"} pending sync${!online ? " — will upload when back online" : ""}`
            : "Working offline — changes will queue for sync"}
        </span>
      </div>
      {online && pending > 0 && (
        <button
          type="button"
          onClick={doSync}
          disabled={busy}
          data-testid="sync-now-btn"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-blue-700 hover:bg-blue-900/50 disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Sync now
        </button>
      )}
    </div>
  );
}
